/**
 * @fileoverview Firebase P2P Test Runner
 *
 * Runs integration tests through Firebase to simulate real P2P gameplay.
 * Two ZodiacEngine instances sync via Firebase, detecting race conditions,
 * timing issues, and state consistency problems.
 */

import { ZodiacFEN } from "../../zodiacEngine/Fen/ZodiacFEN";
import { State } from "../../zodiacEngine/types";
import { IntegrationTest, TestResult, ParsedMove, TestSuite } from "../types";
import { TestStep, TestDelta } from "../LocalTestRunner";
import { parseMove } from "../MoveNotationParser";
import { detectResult } from "../ResultDetector";
import { inferMoveFromFENDelta } from "../../zodiacEngine/Fen/fenDeltaUtils";
import { DualEngineCoordinator } from "./DualEngineCoordinator";
import {
  FirebaseTestConfig,
  FirebaseTestResult,
  FirebaseTestSuiteResult,
  DEFAULT_FIREBASE_TEST_CONFIG,
  categorizeResult,
  FirebaseTestResultCategory,
} from "./FirebaseTestTypes";

/**
 * Run a single integration test through Firebase P2P
 */
export async function runFirebaseTest(
  test: IntegrationTest,
  config?: Partial<FirebaseTestConfig>
): Promise<FirebaseTestResult> {
  const fullConfig = { ...DEFAULT_FIREBASE_TEST_CONFIG, ...config };
  const startTime = performance.now();

  // Handle sequence tests
  if (test.isSequence && test.steps && test.steps.length > 0) {
    return runFirebaseSequenceTest(test, fullConfig);
  }

  // Initialize step tracking
  const steps: TestStep[] = [
    { name: "Initialize Dual Engines", status: "pending" },
    { name: "Setup Firebase Connection", status: "pending" },
    { name: "Parse Move Notation", status: "pending" },
    { name: "Execute Move via Firebase", status: "pending" },
    { name: "Wait for Firebase Sync", status: "pending" },
    { name: "Verify Engine Consistency", status: "pending" },
    { name: "Compare FEN", status: "pending" },
    { name: "Check Game End", status: "pending" },
  ];

  const updateStep = (index: number, status: TestStep["status"], message?: string) => {
    steps[index].status = status;
    steps[index].timestamp = performance.now();
    if (message) steps[index].message = message;
  };

  // Create coordinator
  const coordinator = new DualEngineCoordinator();

  // Result template
  const createResult = (
    passed: boolean,
    lightFEN: string,
    darkFEN: string,
    actualResult: TestResult,
    error?: string,
    extras?: Partial<FirebaseTestResult>
  ): FirebaseTestResult => ({
    test,
    passed,
    actualFEN: lightFEN, // Use light engine FEN as primary
    expectedFEN: test.expectedFEN,
    actualResult,
    expectedResult: test.expectedResult,
    error,
    duration: performance.now() - startTime,
    steps,
    inputFENVerified: false,
    moveNotationParsed: false,
    moveExecuted: false,
    fenMatches: false,
    resultMatches: false,
    deltaMatches: true, // Default true, not checking delta in Firebase tests
    // Firebase-specific fields
    firebaseRoundTrips: coordinator.getRoundTripCount(),
    pieceIdChanges: coordinator.getPieceIdChanges(),
    stateConsistencyChecks: coordinator.getConsistencyChecks(),
    syncTimings: coordinator.getSyncTimings(),
    lightEngineFEN: lightFEN,
    darkEngineFEN: darkFEN,
    enginesConsistent: false,
    ...extras,
  });

  try {
    // Step 1: Initialize dual engines
    updateStep(0, "running");

    // Ensure test players are "placed" (completed 10 placement games)
    // This prevents game over tests from always showing "placement" modal
    await DualEngineCoordinator.ensureTestPlayersArePlaced();

    const gameId = await coordinator.initialize({
      testId: test.name.replace(/\s+/g, "_"),
      inputFEN: test.inputFEN,
      lightKing: test.lightKing,
      darkKing: test.darkKing,
      lightPlayerId: "test-light-player",
      darkPlayerId: "test-dark-player",
      timeControl: test.timeControl,
      gameMode: test.gameMode,
    });
    updateStep(0, "passed", `Engines initialized with gameId: ${gameId.slice(0, 16)}...`);

    // Step 2: Firebase connection established (done in initialize)
    updateStep(1, "passed", "Firebase listeners active on both engines");

    // Add simulated network latency if configured
    if (fullConfig.networkLatency > 0) {
      await delay(fullConfig.networkLatency);
    }

    // Step 3: Parse move notation
    updateStep(2, "running");
    const parsedMove = parseMove(test.move);
    if (!parsedMove) {
      updateStep(2, "failed", `Invalid notation: ${test.move}`);
      for (let i = 3; i < steps.length; i++) updateStep(i, "skipped");
      await coordinator.cleanup();
      return createResult(false, test.inputFEN, test.inputFEN, { gameEnd: false }, `Failed to parse move: ${test.move}`, {
        moveNotationParsed: false,
      });
    }
    updateStep(2, "passed", `${parsedMove.color} ${parsedMove.pieceType} ${test.move}`);

    // Capture before state from both engines
    const beforeLightState = JSON.parse(JSON.stringify(coordinator.getLightEngineState()));
    const beforeLightFEN = coordinator.getLightEngineFEN();

    // Step 4: Execute move on the appropriate engine
    updateStep(3, "running");
    const movingRole = parsedMove.color;
    const moveResult = await coordinator.executeMoveOnEngine(movingRole, parsedMove);

    if (!moveResult.success) {
      updateStep(3, "failed", moveResult.error);
      for (let i = 4; i < steps.length; i++) updateStep(i, "skipped");
      await coordinator.cleanup();
      return createResult(
        false,
        coordinator.getLightEngineFEN(),
        coordinator.getDarkEngineFEN(),
        { gameEnd: false },
        moveResult.error,
        { moveExecuted: false }
      );
    }
    updateStep(3, "passed", "Move executed successfully");

    // Step 5: Wait for Firebase sync
    updateStep(4, "running");
    try {
      // Add network latency simulation
      if (fullConfig.networkLatency > 0) {
        await delay(fullConfig.networkLatency);
      }

      // Wait for both engines to see the same state
      await coordinator.waitForSyncComplete();
      updateStep(4, "passed", `Sync completed after ${coordinator.getRoundTripCount()} round-trip(s)`);
    } catch (syncError) {
      updateStep(4, "failed", syncError instanceof Error ? syncError.message : "Sync timeout");
      for (let i = 5; i < steps.length; i++) updateStep(i, "skipped");
      await coordinator.cleanup();
      return createResult(
        false,
        coordinator.getLightEngineFEN(),
        coordinator.getDarkEngineFEN(),
        { gameEnd: false },
        `Firebase sync failed: ${syncError instanceof Error ? syncError.message : String(syncError)}`,
        { moveExecuted: true }
      );
    }

    // Step 6: Verify engine consistency
    updateStep(5, "running");
    const consistencyChecks = coordinator.verifyConsistency();
    const enginesConsistent = consistencyChecks.every((c) => c.matches);

    if (!enginesConsistent) {
      const mismatches = consistencyChecks.filter((c) => !c.matches);
      updateStep(5, "failed", `Consistency mismatch: ${mismatches.map((m) => m.field).join(", ")}`);
    } else {
      updateStep(5, "passed", "Both engines agree on state");
    }

    // Get final states
    const lightFEN = coordinator.getLightEngineFEN();
    const darkFEN = coordinator.getDarkEngineFEN();
    const afterLightState = coordinator.getLightEngineState();
    const actualResult = detectResult(beforeLightState, afterLightState, parsedMove);

    // Step 7: Compare FEN
    updateStep(6, "running");
    const fenMatches = normalizeFEN(lightFEN) === normalizeFEN(test.expectedFEN);
    updateStep(
      6,
      fenMatches ? "passed" : "failed",
      fenMatches ? "FEN matches expected" : `Expected: ${test.expectedFEN}, Got: ${lightFEN}`
    );

    // Step 8: Check game end
    updateStep(7, "running");
    const resultMatches = compareResults(test.expectedResult, actualResult);
    const expectedEnd = test.expectedResult?.gameEnd || "none";
    const actualEnd = actualResult.gameEnd || "none";
    updateStep(7, resultMatches ? "passed" : "failed", `Expected: ${expectedEnd}, Got: ${actualEnd}`);

    // Build delta
    const actualDelta = buildDelta(beforeLightFEN, lightFEN, beforeLightState, afterLightState, parsedMove);

    // Check bonus turn sync if applicable
    const bonusTurnSync = coordinator.getBonusTurnSync();

    // Calculate overall pass
    const passed = fenMatches && resultMatches && enginesConsistent;

    await coordinator.cleanup();

    return createResult(passed, lightFEN, darkFEN, actualResult, undefined, {
      inputFENVerified: true,
      moveNotationParsed: true,
      moveExecuted: true,
      fenMatches,
      resultMatches,
      enginesConsistent,
      actualDelta,
      bonusTurnSync,
      firebaseRoundTrips: coordinator.getRoundTripCount(),
      pieceIdChanges: coordinator.getPieceIdChanges(),
      stateConsistencyChecks: consistencyChecks,
      syncTimings: coordinator.getSyncTimings(),
    });
  } catch (error) {
    // Mark all pending steps as failed
    steps.forEach((step, i) => {
      if (step.status === "pending" || step.status === "running") {
        updateStep(i, "failed", error instanceof Error ? error.message : String(error));
      }
    });

    await coordinator.cleanup();

    return createResult(
      false,
      test.inputFEN,
      test.inputFEN,
      { gameEnd: false },
      error instanceof Error ? error.message : String(error),
      {
        inputFENVerified: false,
        moveNotationParsed: false,
        moveExecuted: false,
        fenMatches: false,
        resultMatches: false,
        enginesConsistent: false,
      }
    );
  }
}

/**
 * Run a sequence test through Firebase P2P
 */
async function runFirebaseSequenceTest(
  test: IntegrationTest,
  config: Required<Omit<FirebaseTestConfig, "test">>
): Promise<FirebaseTestResult> {
  const startTime = performance.now();
  const coordinator = new DualEngineCoordinator();

  const steps: TestStep[] = [
    { name: "Initialize Dual Engines", status: "pending" },
    { name: "Execute Sequence", status: "pending" },
  ];

  // Track sequence steps for UI display
  const sequenceSteps: Array<{
    stepNumber: number;
    move: string;
    note?: string;
    expectedFEN: string;
    actualFEN: string;
    fenMatches: boolean;
    expectedDelta?: TestDelta;
    actualDelta?: TestDelta;
    deltaMatches: boolean;
    expectedStatus?: string[];
    actualStatus?: string[];
    expectedResult?: TestResult;
    actualResult?: TestResult;
    passed: boolean;
    error?: string;
  }> = [];

  const updateStep = (index: number, status: TestStep["status"], message?: string) => {
    steps[index].status = status;
    steps[index].timestamp = performance.now();
    if (message) steps[index].message = message;
  };

  try {
    // Initialize
    updateStep(0, "running");

    // Ensure test players are "placed" (completed 10 placement games)
    await DualEngineCoordinator.ensureTestPlayersArePlaced();

    await coordinator.initialize({
      testId: test.name.replace(/\s+/g, "_"),
      inputFEN: test.inputFEN,
      lightKing: test.lightKing,
      darkKing: test.darkKing,
      lightPlayerId: "test-light-player",
      darkPlayerId: "test-dark-player",
    });
    updateStep(0, "passed", "Engines initialized");

    // Execute sequence
    updateStep(1, "running");
    let allStepsPassed = true;
    let currentFEN = test.inputFEN;

    for (let i = 0; i < test.steps!.length; i++) {
      const step = test.steps![i];
      const stepNumber = i + 1;

      // Parse move
      const parsedMove = parseMove(step.move);
      if (!parsedMove) {
        sequenceSteps.push({
          stepNumber,
          move: step.move,
          note: step.note,
          expectedFEN: step.expectedFEN,
          actualFEN: currentFEN,
          fenMatches: false,
          deltaMatches: false,
          expectedDelta: step.expectedDelta,
          expectedStatus: step.expectedStatus,
          expectedResult: step.expectedResult,
          passed: false,
          error: `Failed to parse move: ${step.move}`,
        });
        allStepsPassed = false;
        break;
      }

      // Capture before state
      const beforeFEN = coordinator.getLightEngineFEN();
      const beforeState = coordinator.getLightEngineState();

      // Execute on appropriate engine
      const movingRole = parsedMove.color;
      const moveResult = await coordinator.executeMoveOnEngine(movingRole, parsedMove);

      if (!moveResult.success) {
        sequenceSteps.push({
          stepNumber,
          move: step.move,
          note: step.note,
          expectedFEN: step.expectedFEN,
          actualFEN: currentFEN,
          fenMatches: false,
          deltaMatches: false,
          expectedDelta: step.expectedDelta,
          expectedStatus: step.expectedStatus,
          expectedResult: step.expectedResult,
          passed: false,
          error: moveResult.error,
        });
        allStepsPassed = false;
        break;
      }

      // Wait for sync
      if (config.networkLatency > 0) {
        await delay(config.networkLatency);
      }
      await coordinator.waitForSyncComplete();

      // Verify consistency
      const checks = coordinator.verifyConsistency();
      if (!checks.every((c) => c.matches)) {
        const mismatches = checks.filter(c => !c.matches).map(c => c.field).join(", ");
        const actualFEN = coordinator.getLightEngineFEN();
        sequenceSteps.push({
          stepNumber,
          move: step.move,
          note: step.note,
          expectedFEN: step.expectedFEN,
          actualFEN,
          fenMatches: false,
          deltaMatches: false,
          expectedDelta: step.expectedDelta,
          expectedStatus: step.expectedStatus,
          expectedResult: step.expectedResult,
          passed: false,
          error: `Consistency mismatch: ${mismatches}`,
        });
        allStepsPassed = false;
        break;
      }

      // Get actual state
      const actualFEN = coordinator.getLightEngineFEN();
      const afterState = coordinator.getLightEngineState();
      const actualNorm = normalizeFEN(actualFEN);
      const expectedNorm = normalizeFEN(step.expectedFEN);


      // Build delta
      const actualDelta = buildDelta(beforeFEN, actualFEN, beforeState, afterState, parsedMove);

      // Check FEN match
      const fenMatches = actualNorm === expectedNorm;

      // Compare delta
      const deltaMatches = !step.expectedDelta || compareDelta(step.expectedDelta, actualDelta);

      // Extract actual status from FEN
      const actualStatus = extractStatusFromFEN(actualFEN);

      // Detect result if game ended
      const actualResult = actualDelta.gameEnded
        ? detectResult(beforeState, afterState, parsedMove)
        : { gameEnd: false as const };

      // Compare result
      const resultMatches = !step.expectedResult || compareResults(step.expectedResult, actualResult);

      const stepPassed = fenMatches && deltaMatches && resultMatches;

      sequenceSteps.push({
        stepNumber,
        move: step.move,
        note: step.note,
        expectedFEN: step.expectedFEN,
        actualFEN,
        fenMatches,
        expectedDelta: step.expectedDelta,
        actualDelta,
        deltaMatches,
        expectedStatus: step.expectedStatus,
        actualStatus,
        expectedResult: step.expectedResult,
        actualResult,
        passed: stepPassed,
      });

      if (!stepPassed) {
        allStepsPassed = false;
      } else {
      }

      currentFEN = actualFEN;
    }

    if (!allStepsPassed) {
      const failedStep = sequenceSteps.find(s => !s.passed);
    } else {
    }

    updateStep(1, allStepsPassed ? "passed" : "failed",
      `Executed ${sequenceSteps.length} steps`);

    const lightFEN = coordinator.getLightEngineFEN();
    const darkFEN = coordinator.getDarkEngineFEN();
    const enginesConsistent = coordinator.areEnginesConsistent();

    await coordinator.cleanup();

    return {
      test,
      passed: allStepsPassed && enginesConsistent,
      actualFEN: lightFEN,
      expectedFEN: test.expectedFEN,
      actualResult: { gameEnd: false },
      expectedResult: test.expectedResult,
      duration: performance.now() - startTime,
      steps,
      sequenceSteps,
      inputFENVerified: true,
      moveNotationParsed: true,
      moveExecuted: true,
      fenMatches: allStepsPassed,
      resultMatches: true,
      deltaMatches: true,
      isSequence: true,
      firebaseRoundTrips: coordinator.getRoundTripCount(),
      pieceIdChanges: coordinator.getPieceIdChanges(),
      stateConsistencyChecks: coordinator.getConsistencyChecks(),
      syncTimings: coordinator.getSyncTimings(),
      lightEngineFEN: lightFEN,
      darkEngineFEN: darkFEN,
      enginesConsistent,
    };
  } catch (error) {
    await coordinator.cleanup();
    return {
      test,
      passed: false,
      actualFEN: test.inputFEN,
      expectedFEN: test.expectedFEN,
      actualResult: { gameEnd: false },
      expectedResult: test.expectedResult,
      error: error instanceof Error ? error.message : String(error),
      duration: performance.now() - startTime,
      steps,
      sequenceSteps,
      inputFENVerified: false,
      moveNotationParsed: false,
      moveExecuted: false,
      fenMatches: false,
      resultMatches: false,
      deltaMatches: false,
      isSequence: true,
      firebaseRoundTrips: 0,
      pieceIdChanges: [],
      stateConsistencyChecks: [],
      syncTimings: [],
      lightEngineFEN: test.inputFEN,
      darkEngineFEN: test.inputFEN,
      enginesConsistent: false,
    };
  }
}

/**
 * Compare expected and actual delta
 */
function compareDelta(expected: TestDelta | undefined, actual: TestDelta): boolean {
  if (!expected) return true;

  if (expected.pieceMoved !== undefined && expected.pieceMoved !== actual.pieceMoved) return false;
  if (expected.pieceCaptured !== undefined && expected.pieceCaptured !== actual.pieceCaptured) return false;
  if (expected.statusChanged !== undefined && expected.statusChanged !== actual.statusChanged) return false;
  if (expected.turnChanged !== undefined && expected.turnChanged !== actual.turnChanged) return false;
  if (expected.bonusTurn !== undefined && expected.bonusTurn !== actual.bonusTurn) return false;
  if (expected.gameEnded !== undefined && expected.gameEnded !== actual.gameEnded) return false;

  return true;
}

/**
 * Extract status effects from FEN notation
 */
function extractStatusFromFEN(fen: string): string[] {
  const statuses: string[] = [];
  let fenString = fen.startsWith("d") || fen.startsWith("l") ? fen.substring(1) : fen;
  const columns = fenString.split("/");
  const columnNames = ["A", "B", "C", "D", "E"];
  const pieceNames: Record<string, string> = {
    B: "Bear", C: "Crane", F: "Frog", U: "Fungi", G: "Goat", M: "Monkey",
    O: "Octopus", P: "Penguin", R: "Rhino", S: "Snake", T: "Tree", W: "Wolf", V: "Virus"
  };

  for (let colIndex = 0; colIndex < columns.length; colIndex++) {
    const column = columns[colIndex];
    let rowIndex = 0;

    for (let i = 0; i < column.length; i++) {
      const char = column[i];
      if (!isNaN(parseInt(char))) { rowIndex += parseInt(char); continue; }
      if (char === '+') continue;

      const upperChar = char.toUpperCase();
      if ("BCFUGMOPRSTWV".includes(upperChar)) {
        const isLight = char === upperChar;
        const color = isLight ? "Light" : "Dark";
        const pieceName = pieceNames[upperChar] || "Unknown";
        const statusSymbols: string[] = [];

        let j = i + 1;
        while (j < column.length) {
          const nextChar = column[j];
          if (nextChar === '⛓') statusSymbols.push("Entangled");
          else if (nextChar === '☣' || nextChar === '!') statusSymbols.push("Poisoned");
          else if (nextChar === '⚘' || nextChar === '#') statusSymbols.push("Meadowed");
          else if (nextChar === 'z' || nextChar === '☠') statusSymbols.push("Zombified");
          else if ("↑↗→↘↓↙←↖".includes(nextChar)) { j++; continue; }
          else if ("BCFUGMOPRSTWV".toUpperCase().includes(nextChar.toUpperCase()) || !isNaN(parseInt(nextChar))) break;
          j++;
        }

        const relevantStatuses = statusSymbols.filter(s => !["King", "Ability Ready", "Unmoved"].includes(s));
        if (relevantStatuses.length > 0) {
          statuses.push(`${color} ${pieceName}: ${relevantStatuses.join(", ")}`);
        }
        rowIndex++;
      }
    }
  }
  return statuses;
}

/**
 * Run an entire test suite through Firebase P2P
 */
export async function runFirebaseTestSuite(
  suite: TestSuite,
  config?: Partial<FirebaseTestConfig>
): Promise<FirebaseTestSuiteResult> {
  const startTime = performance.now();
  const results: FirebaseTestResult[] = [];

  for (const test of suite.tests) {
    const result = await runFirebaseTest(test, config);
    results.push(result);

    // Small delay between tests to avoid Firebase rate limiting
    await delay(100);
  }

  // Calculate statistics
  const passed = results.filter((r) => r.passed).length;
  const failed = results.filter((r) => !r.passed).length;

  // Categorize failures
  const failureBreakdown: Record<FirebaseTestResultCategory, number> = {
    PASS: 0,
    FAIL_FEN: 0,
    FAIL_RESULT: 0,
    FAIL_CONSISTENCY: 0,
    FAIL_SYNC_TIMEOUT: 0,
    FAIL_ID_RESOLUTION: 0,
    FAIL_BONUS_TURN_DESYNC: 0,
    FAIL_ERROR: 0,
  };

  for (const result of results) {
    const category = categorizeResult(result);
    failureBreakdown[category]++;
  }

  // Calculate sync statistics
  const totalRoundTrips = results.reduce((sum, r) => sum + r.firebaseRoundTrips, 0);
  const allSyncTimings = results.flatMap((r) => r.syncTimings);
  const averageSyncTime =
    allSyncTimings.length > 0
      ? allSyncTimings.reduce((sum, t) => sum + t.durationMs, 0) / allSyncTimings.length
      : 0;

  return {
    suiteName: suite.name,
    results,
    passed,
    failed,
    duration: performance.now() - startTime,
    failureBreakdown,
    totalRoundTrips,
    averageSyncTime,
  };
}

/**
 * Run all test suites through Firebase P2P
 */
export async function runAllFirebaseTests(
  suites: TestSuite[],
  config?: Partial<FirebaseTestConfig>
): Promise<FirebaseTestSuiteResult[]> {
  const results: FirebaseTestSuiteResult[] = [];

  for (const suite of suites) {
    const result = await runFirebaseTestSuite(suite, config);
    results.push(result);
  }

  return results;
}

// Helper functions

function normalizeFEN(fen: string): string {
  let normalized = fen.trim().replace(/\/+$/, "");
  if (normalized.startsWith("d") || normalized.startsWith("l")) {
    normalized = normalized.substring(1);
  }
  normalized = normalized.replace(/[✳️*○$⛓☠!☣#z↑↓←→↖↗↘↙]/g, "");
  normalized = normalized.replace(/([A-Z])♛/g, "$1K");
  normalized = normalized.replace(/([a-z])♕/g, "$1k");
  normalized = normalized.replace(/([A-Z])♕/g, "$1K");
  normalized = normalized.replace(/([a-z])♛/g, "$1k");
  return normalized;
}

function compareResults(expected: TestResult | undefined, actual: TestResult): boolean {
  if (!expected) {
    return actual.gameEnd === false;
  }
  if (expected.gameEnd !== actual.gameEnd) {
    return false;
  }
  if (expected.winner && expected.winner !== actual.winner) {
    return false;
  }
  return true;
}

function buildDelta(
  beforeFEN: string,
  afterFEN: string,
  beforeState: any,
  afterState: any,
  parsedMove?: ParsedMove
): TestDelta {
  const delta: TestDelta = {};

  if (parsedMove?.isSpecialCommand && parsedMove.specialCommand === "timeout") {
    delta.pieceMoved = `${parsedMove.color === "light" ? "Light" : "Dark"} ran out of time`;
    delta.turnChanged = false;
    delta.gameEnded = true;
    delta.bonusTurn = false;
    delta.statusChanged = false;
    return delta;
  }

  const wasLightMove = beforeState.isLightTurn;
  delta.pieceMoved = inferMoveFromFENDelta(beforeFEN, afterFEN, wasLightMove);
  delta.turnChanged = beforeState.isLightTurn !== afterState.isLightTurn;
  // Check for game end - also check lightWon since state might not be updated to PostGame yet
  delta.gameEnded = afterState.state === State.PostGame ||
                    afterState.state === State.Terminated ||
                    afterState.state === State.Draw ||
                    afterState.lightWon !== undefined;
  delta.bonusTurn = (beforeState.isLightTurn === afterState.isLightTurn) && !delta.gameEnded;

  // Check for status changes by comparing status symbols in FEN
  const beforeStatusCount = countStatusSymbols(beforeFEN);
  const afterStatusCount = countStatusSymbols(afterFEN);
  delta.statusChanged = beforeStatusCount !== afterStatusCount;

  return delta;
}

/**
 * Count status symbols in FEN string
 */
function countStatusSymbols(fen: string): number {
  // Count all status effect symbols
  // !☣☠ = poison, ⛓ = entangle, z = zombify, # = meadow (ascii), ⚘ = meadow (unicode), $ = faint/special, *✳️ = ability unused
  // +v/+V = virus infection
  // NOTE: + (piece stacking) is NOT counted UNLESS it's followed by v or V (virus infection)
  const statusRegex = /[!☣⛓z☠#$*✳️⚘]/g;
  const matches = fen.match(statusRegex);
  let count = matches ? matches.length : 0;

  // Count virus infections (+v or +V)
  const virusInfectionRegex = /\+[vV]/g;
  const virusMatches = fen.match(virusInfectionRegex);
  count += virusMatches ? virusMatches.length : 0;

  return count;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
